export function zonedTimeToUtcFallback(
    localYMDHM: string,
    timeZone: string
): Date {
    const [datePart, timePart = '00:00'] = localYMDHM.split(' ');
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = (timePart || '00:00').split(':').map(Number);

    const MS = 1000;
    const MINUTE = 60 * MS;
    const HOUR = 60 * MINUTE;
    const SEARCH_WINDOW = 72 * HOUR;

    const targetParts = [y, m, d, hh, mm];

    const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });

    function partsFor(ts: number) {
        const parts = fmt.formatToParts(new Date(ts));
        const map: Record<string, string> = {};
        for (const p of parts) {
            if (p.type !== 'literal') map[p.type] = p.value;
        }
        return [
            Number(map.year),
            Number(map.month),
            Number(map.day),
            Number(map.hour),
            Number(map.minute),
        ];
    }

    function cmp(a: number[], b: number[]) {
        for (let i = 0; i < a.length; i++) {
            if (a[i] < b[i]) return -1;
            if (a[i] > b[i]) return 1;
        }
        return 0;
    }

    // center the search around the naive UTC instant for the same numeric components
    const center = Date.UTC(y, m - 1, d, hh, mm);
    let low = center - SEARCH_WINDOW;
    let high = center + SEARCH_WINDOW;
    let foundTs: number | null = null;

    // Binary search for monotonic mapping from UTC -> local parts
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const p = partsFor(mid);
        const c = cmp(p, targetParts);
        if (c === 0) {
            foundTs = mid;
            break;
        } else if (c < 0) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    if (foundTs === null) {
        // last-ditch linear check across the window (shouldn't be necessary but safe)
        for (
            let ts = center - SEARCH_WINDOW;
            ts <= center + SEARCH_WINDOW;
            ts += MINUTE
        ) {
            const p = partsFor(ts);
            if (cmp(p, targetParts) === 0) {
                foundTs = ts;
                break;
            }
        }
    }

    if (foundTs === null) {
        throw new Error(
            `Could not resolve zoned time for ${localYMDHM} in ${timeZone}`
        );
    }

    // Align to start of minute for deterministic output (zero seconds, zero ms)
    foundTs = Math.floor(foundTs / (60 * 1000)) * (60 * 1000);

    return new Date(foundTs);
}
